import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Stack, Typography } from '@packages/shared';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ServiceLayer } from '@packages/cms-components';
import { CarrierDeliveryInfoFragmentFragmentDoc } from '@packages/gql/generated/shopping/CarrierDeliveryInfoFragmentFragmentDoc';
import { TextWithDialog } from '../TextWithDialog';
import { carrierDeliveryInfoMessages } from './messages';

/* GraphQL */ `
  fragment CarrierDeliveryInfoFragment on Delivery {
    deliverySize
  }
`;

export type CarrierDeliveryInfoProps = {
  data: FragmentType<typeof CarrierDeliveryInfoFragmentFragmentDoc>;
};

/**
 * <p>
 * The <code>CarrierDeliveryInfo</code> component is used to display all available carrier delivery messages.
 * Therefore it iterates over the <code>deliveryData</code> values and returns the corresponding info text.
 * </p>
 * <p>
 * As of right now there are 2 sizes which are being delivery by a carrier:
 * <ul>
 *  <li>S - articles are delivered into home</li>
 *  <li>L - articles are delivered to property boundary</li>
 * </ul>
 * </p>
 * <p>
 * The content of the dialog is being loaded from the Inspire CMS.
 * </p>
 * */

export const CarrierDeliveryInfo = ({ data }: CarrierDeliveryInfoProps) => {
  const { deliverySize } = getFragmentData(CarrierDeliveryInfoFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();

  const onClickEventHandler = () => {
    trackViewLayer(`${formatMessage(carrierDeliveryInfoMessages.dialogHeadline)} ${deliverySize}`);
  };

  if (!(deliverySize === 'S' || deliverySize === 'L')) return null;

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {formatMessage(
          deliverySize === 'S'
            ? carrierDeliveryInfoMessages.carrierDeliveryS
            : carrierDeliveryInfoMessages.carrierDeliveryL,
          {
            link: (msg: ReactNode) => (
              <TextWithDialog
                message={msg}
                dialogTitle={formatMessage(carrierDeliveryInfoMessages.dialogHeadline)}
                dialogContent={
                  <ServiceLayer serviceLayerId={`delivery_spedition-layer-${deliverySize}`} />
                }
                onClickHandler={onClickEventHandler}
                variant="body2"
                displayTrackingProps={{ category: 'dv-service-layer-carrier-delivery-info' }}
              />
            ),
          },
        )}
      </Typography>
    </Stack>
  );
};
